/**
 * AGI Credit Betting Component - Betting interface for AGI Markets
 * Uses credits instead of blockchain tokens
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Coins, 
  TrendingUp, 
  TrendingDown, 
  Loader, 
  CheckCircle,
  Sparkles,
  AlertCircle,
  Shield,
  RefreshCw,
  Trash2,
  Trophy
} from 'lucide-react'
import toast from 'react-hot-toast'
import { useAgiAuthStore, agiApi } from '../store/agiAuthStore'

interface AgiCreditBettingProps {
  marketId: number
  question: string
  totalYes: number
  totalNo: number
  status: string
  bettingEndsAt: string
  isAdmin?: boolean
  onBetPlaced?: () => void
  onResolved?: () => void
}

export default function AgiCreditBetting({
  marketId,
  question,
  totalYes,
  totalNo,
  status,
  bettingEndsAt,
  isAdmin,
  onBetPlaced,
  onResolved
}: AgiCreditBettingProps) {
  const { agiUser, isAgiAuthenticated, agiToken, updateCredits } = useAgiAuthStore()
  const [betAmount, setBetAmount] = useState<number>(10)
  const [selectedOutcome, setSelectedOutcome] = useState<'yes' | 'no' | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [isResettingCredits, setIsResettingCredits] = useState(false)
  const [isDeletingMarket, setIsDeletingMarket] = useState(false)
  // Track BOTH YES and NO bets separately (user can bet on both sides)
  const [userYesBet, setUserYesBet] = useState<{ amount: number } | null>(null)
  const [userNoBet, setUserNoBet] = useState<{ amount: number } | null>(null)
  
  // Fetch user's existing bets on this market (both YES and NO)
  useEffect(() => {
    const fetchUserBets = async () => {
      if (!isAgiAuthenticated || !agiToken) return
      try {
        agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
        const response = await agiApi.get(`/api/agi/markets/${marketId}`)
        // Set both YES and NO bets from backend response
        if (response.data.userYesBet) {
          setUserYesBet({ amount: response.data.userYesBet.amount })
        } else {
          setUserYesBet(null)
        }
        if (response.data.userNoBet) {
          setUserNoBet({ amount: response.data.userNoBet.amount })
        } else {
          setUserNoBet(null)
        }
      } catch (e) {
        // Ignore - user may not have bet
      }
    }
    fetchUserBets()
  }, [marketId, isAgiAuthenticated, agiToken])
  
  // Calculate odds
  const total = totalYes + totalNo
  const yesPercent = total > 0 ? Math.round((totalYes / total) * 100) : 50
  const noPercent = total > 0 ? Math.round((totalNo / total) * 100) : 50
  
  // Calculate potential payout for a bet on a specific side
  const calculatePotentialPayout = (betOutcome: number, betAmt: number): number => {
    const totalPool = totalYes + totalNo
    if (totalPool === 0) return betAmt
    
    const userSidePool = betOutcome === 1 ? totalYes : totalNo
    const losingSidePool = betOutcome === 1 ? totalNo : totalYes
    if (userSidePool === 0) return totalPool // Would get entire pool
    
    const proportion = betAmt / userSidePool
    const winnings = Math.floor(losingSidePool * proportion)
    return betAmt + winnings
  }
  
  // Total invested across both sides
  const totalInvested = (userYesBet?.amount || 0) + (userNoBet?.amount || 0)
  const hasBets = userYesBet || userNoBet
  const hasBothSides = userYesBet && userNoBet
  
  // Calculate NET outcomes for each scenario (accounting for bets on BOTH sides)
  // If YES wins: User gets YES payout but LOSES NO bet
  const yesWinPayout = userYesBet ? calculatePotentialPayout(1, userYesBet.amount) : 0
  const yesWinNet = yesWinPayout - totalInvested  // Subtract total invested to get true profit/loss
  
  // If NO wins: User gets NO payout but LOSES YES bet
  const noWinPayout = userNoBet ? calculatePotentialPayout(0, userNoBet.amount) : 0
  const noWinNet = noWinPayout - totalInvested  // Subtract total invested to get true profit/loss
  
  // Check if betting is still open
  const bettingOpen = status === 'active' && new Date(bettingEndsAt) > new Date()
  const canBet = isAgiAuthenticated && agiUser?.isAgi && bettingOpen
  const isResolved = status === 'resolved'
  
  const handlePlaceBet = async () => {
    if (!selectedOutcome || !canBet) return
    
    if (betAmount > (agiUser?.credits || 0)) {
      toast.error('Insufficient credits!')
      return
    }
    
    setIsSubmitting(true)
    
    try {
      agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
      
      const response = await agiApi.post(`/api/agi/markets/${marketId}/bet`, {
        outcome: selectedOutcome === 'yes' ? 1 : 0,
        amount: betAmount
      })
      
      if (response.data.success) {
        toast.success(`Bet ${betAmount} credits on ${selectedOutcome.toUpperCase()}! 🎯`)
        updateCredits(response.data.remainingCredits)
        setSelectedOutcome(null)
        setBetAmount(10)
        onBetPlaced?.()
      }
    } catch (error: any) {
      console.error('Bet error:', error)
      toast.error(error.response?.data?.error || 'Failed to place bet')
    } finally {
      setIsSubmitting(false)
    }
  }
  
  const handleResolve = async (outcome: 'yes' | 'no') => {
    if (!isAdmin) return
    
    if (!confirm(`Are you sure you want to resolve this market as ${outcome.toUpperCase()}? This cannot be undone.`)) {
      return
    }
    
    setIsResolving(true)
    
    try {
      agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
      
      const response = await agiApi.post(`/api/agi/markets/${marketId}/resolve`, {
        outcome: outcome === 'yes' ? 1 : 0
      })
      
      if (response.data.success) {
        toast.success(`Market resolved as ${outcome.toUpperCase()}! 🎉`)
        onResolved?.()
      }
    } catch (error: any) {
      console.error('Resolve error:', error)
      toast.error(error.response?.data?.error || 'Failed to resolve market')
    } finally {
      setIsResolving(false)
    }
  }
  
  // Admin: Reset credits to 500
  const handleResetCredits = async () => {
    if (!isAdmin) return
    
    setIsResettingCredits(true)
    
    try {
      agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
      const response = await agiApi.post('/api/agi/admin/reset-credits')
      
      if (response.data.success) {
        toast.success(`Credits reset to ${response.data.credits}! 💰`)
        updateCredits(response.data.credits)
      }
    } catch (error: any) {
      console.error('Reset credits error:', error)
      toast.error(error.response?.data?.error || 'Failed to reset credits')
    } finally {
      setIsResettingCredits(false)
    }
  }
  
  // Admin: Delete this market
  const handleDeleteMarket = async () => {
    if (!isAdmin) return
    
    if (!confirm('Are you sure you want to DELETE this market? All bets will be refunded. This cannot be undone.')) {
      return
    }
    
    setIsDeletingMarket(true)
    
    try {
      agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
      const response = await agiApi.delete(`/api/agi/markets/${marketId}`)
      
      if (response.data.success) {
        toast.success('Market deleted! Redirecting...')
        // Redirect to markets page
        window.location.href = '/markets?category=agi_manual'
      }
    } catch (error: any) {
      console.error('Delete market error:', error)
      toast.error(error.response?.data?.error || 'Failed to delete market')
    } finally {
      setIsDeletingMarket(false)
    }
  }
  
  if (!isAgiAuthenticated || !agiUser?.isAgi) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
        <Sparkles className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-white mb-2">AGI Markets</h3>
        <p className="text-gray-400">Login with your @agi.live email to bet on this market</p>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <h3 className="text-lg font-bold text-white">AGI Market Betting</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Coins className="w-4 h-4 text-yellow-400" />
          <span className="font-bold text-yellow-400">{agiUser.credits} credits</span>
        </div>
      </div>
      
      {/* Odds Display */}
      <div className="mb-6">
        <div className="flex justify-between text-sm font-bold mb-2">
          <span className="text-emerald-400 flex items-center">
            <TrendingUp className="w-4 h-4 mr-1" />
            YES {yesPercent}%
          </span>
          <span className="text-rose-400 flex items-center">
            <TrendingDown className="w-4 h-4 mr-1" />
            NO {noPercent}%
          </span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-4 overflow-hidden">
          <div 
            className="h-full transition-all duration-500 bg-gradient-to-r from-emerald-500 to-rose-500"
            style={{ width: `${yesPercent}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{totalYes} credits</span>
          <span>{totalNo} credits</span>
        </div>
      </div>
      
      {/* User's Position - Show ALL bets (YES and/or NO) */}
      {hasBets && (
        <div className="mb-6 p-4 rounded-xl bg-gray-800/60 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Trophy className="w-5 h-5 text-purple-400" />
              <span className="font-bold text-white">Your Position</span>
            </div>
            <div className="flex items-center space-x-2">
              {userYesBet && (
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400">
                  YES
                </span>
              )}
              {userNoBet && (
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-rose-500/20 text-rose-400">
                  NO
                </span>
              )}
            </div>
          </div>
          
          {/* Current Bets */}
          <div className={`grid ${hasBothSides ? 'grid-cols-3' : 'grid-cols-2'} gap-3 text-center mb-4`}>
            {userYesBet && (
              <div className="bg-emerald-500/10 rounded-lg p-2">
                <div className="text-xs text-gray-400 uppercase tracking-wider">YES Bet</div>
                <div className="text-lg font-bold text-emerald-400">{userYesBet.amount}</div>
                <div className="text-xs text-gray-500">credits</div>
              </div>
            )}
            {userNoBet && (
              <div className="bg-rose-500/10 rounded-lg p-2">
                <div className="text-xs text-gray-400 uppercase tracking-wider">NO Bet</div>
                <div className="text-lg font-bold text-rose-400">{userNoBet.amount}</div>
                <div className="text-xs text-gray-500">credits</div>
              </div>
            )}
            <div className="bg-gray-700/30 rounded-lg p-2">
              <div className="text-xs text-gray-400 uppercase tracking-wider">Total Invested</div>
              <div className="text-lg font-bold text-white">{totalInvested}</div>
              <div className="text-xs text-gray-500">credits</div>
            </div>
          </div>
          
          {/* Potential Outcomes */}
          <div className="border-t border-purple-500/20 pt-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-3 text-center">Potential Outcomes</div>
            <div className="grid grid-cols-2 gap-3">
              {/* If YES Wins */}
              <div className={`rounded-lg p-3 ${userYesBet ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-gray-700/20 border border-gray-700'}`}>
                <div className="text-xs font-bold text-emerald-400 mb-2 flex items-center">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  IF YES WINS
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-white">{yesWinPayout}</div>
                  <div className="text-xs text-gray-500 mb-1">credits back</div>
                  <div className={`text-sm font-bold ${yesWinNet >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {yesWinNet >= 0 ? '+' : ''}{yesWinNet} net
                  </div>
                </div>
              </div>
              
              {/* If NO Wins */}
              <div className={`rounded-lg p-3 ${userNoBet ? 'bg-rose-500/10 border border-rose-500/30' : 'bg-gray-700/20 border border-gray-700'}`}>
                <div className="text-xs font-bold text-rose-400 mb-2 flex items-center">
                  <TrendingDown className="w-3 h-3 mr-1" />
                  IF NO WINS
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-white">{noWinPayout}</div>
                  <div className="text-xs text-gray-500 mb-1">credits back</div>
                  <div className={`text-sm font-bold ${noWinNet >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {noWinNet >= 0 ? '+' : ''}{noWinNet} net
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {hasBothSides && (
            <div className="mt-3 pt-3 border-t border-purple-500/20 text-center">
              <p className="text-xs text-yellow-400">
                ⚠️ You have bets on both sides. Your net profit depends on which outcome wins.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Betting Interface or Resolved State */}
      {isResolved ? (
        <div className="text-center py-4">
          <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
          <p className="text-lg font-bold text-white">Market Resolved</p>
          <p className="text-gray-400">Check the results above</p>
        </div>
      ) : canBet ? (
        <>
          {/* Outcome Selection */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <button
              onClick={() => setSelectedOutcome('yes')}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedOutcome === 'yes'
                  ? 'border-emerald-500 bg-emerald-500/20'
                  : 'border-gray-700 hover:border-emerald-500/50'
              }`}
            >
              <TrendingUp className={`w-6 h-6 mx-auto mb-2 ${selectedOutcome === 'yes' ? 'text-emerald-400' : 'text-gray-500'}`} />
              <div className="text-lg font-bold text-white">YES</div>
              <div className="text-sm text-gray-400">{yesPercent}% chance</div>
            </button>
            
            <button
              onClick={() => setSelectedOutcome('no')}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedOutcome === 'no'
                  ? 'border-rose-500 bg-rose-500/20'
                  : 'border-gray-700 hover:border-rose-500/50'
              }`}
            >
              <TrendingDown className={`w-6 h-6 mx-auto mb-2 ${selectedOutcome === 'no' ? 'text-rose-400' : 'text-gray-500'}`} />
              <div className="text-lg font-bold text-white">NO</div>
              <div className="text-sm text-gray-400">{noPercent}% chance</div>
            </button>
          </div>
          
          {/* Amount Input */}
          {selectedOutcome && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm text-gray-400 mb-2">Bet Amount (credits)</label>
                <div className="flex space-x-2">
                  {[10, 25, 50, 100].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setBetAmount(amount)}
                      disabled={amount > (agiUser.credits || 0)}
                      className={`flex-1 py-2 rounded-lg font-semibold transition-all ${
                        betAmount === amount
                          ? 'bg-yellow-500 text-black'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-50'
                      }`}
                    >
                      {amount}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <input
                  type="number"
                  value={betAmount}
                  onChange={e => setBetAmount(Math.max(1, Math.min(agiUser.credits || 0, parseInt(e.target.value) || 0)))}
                  min={1}
                  max={agiUser.credits || 0}
                  className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-center"
                />
                <button
                  onClick={() => setBetAmount(agiUser.credits || 0)}
                  className="text-sm text-yellow-400 hover:text-yellow-300"
                >
                  Max ({agiUser.credits})
                </button>
              </div>
              
              <button
                onClick={handlePlaceBet}
                disabled={isSubmitting || betAmount < 1}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center space-x-2 ${
                  selectedOutcome === 'yes'
                    ? 'bg-emerald-500 hover:bg-emerald-400 text-white'
                    : 'bg-rose-500 hover:bg-rose-400 text-white'
                } disabled:opacity-50`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Placing bet...</span>
                  </>
                ) : (
                  <>
                    <Coins className="w-5 h-5" />
                    <span>Bet {betAmount} on {selectedOutcome.toUpperCase()}</span>
                  </>
                )}
              </button>
            </motion.div>
          )}
        </>
      ) : (
        <div className="text-center py-4">
          <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
          <p className="text-gray-400">
            {!bettingOpen ? 'Betting has closed for this market' : 'Cannot bet on this market'}
          </p>
        </div>
      )}
      
      {/* Admin Resolve Section */}
      {isAdmin && status === 'active' && (
        <div className="mt-6 pt-6 border-t border-yellow-500/30">
          <div className="flex items-center space-x-2 mb-4">
            <Shield className="w-5 h-5 text-purple-400" />
            <span className="font-bold text-purple-400">Admin: Resolve Market</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => handleResolve('yes')}
              disabled={isResolving}
              className="py-3 rounded-xl bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 font-bold hover:bg-emerald-500/30 transition-all disabled:opacity-50"
            >
              {isResolving ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Resolve YES'}
            </button>
            
            <button
              onClick={() => handleResolve('no')}
              disabled={isResolving}
              className="py-3 rounded-xl bg-rose-500/20 border border-rose-500/50 text-rose-400 font-bold hover:bg-rose-500/30 transition-all disabled:opacity-50"
            >
              {isResolving ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'Resolve NO'}
            </button>
          </div>
        </div>
      )}
      
      {/* Admin Testing Tools */}
      {isAdmin && (
        <div className="mt-4 pt-4 border-t border-gray-700/50">
          <div className="flex items-center space-x-2 mb-3">
            <Shield className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Admin Testing Tools</span>
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={handleResetCredits}
              disabled={isResettingCredits}
              className="flex-1 py-2 px-3 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-700 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isResettingCredits ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Reset Credits (500)</span>
                </>
              )}
            </button>
            
            <button
              onClick={handleDeleteMarket}
              disabled={isDeletingMarket}
              className="py-2 px-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-all disabled:opacity-50 flex items-center space-x-2"
            >
              {isDeletingMarket ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
